import { Auth } from './auth';
import { stripLocale, localeFromPath, setLocale, getLocale, withLocale } from './i18n';
import { OG_LOCALE } from './i18n/types';
import { applyHreflangLinks } from './i18n/hreflang';
import { captureUtm } from './utm';
import { ModalManager } from './modals/manager';
import { backButton as tgBackButton, haptic as tgHaptic } from './telegram-mini-app';

export interface RouteContext {
  path: string;
  params: Record<string, string>;
}

export interface RouteUnmount {
  (): void;
}

export interface RouteDef {
  path: string;
  authRequired?: boolean;
  routeType?: 'home' | 'prices' | 'guides' | 'faq' | 'blog' | 'article' | 'country' | '404';
  metaTitle: string;
  metaDesc?: string;
  navKey?: string;
  jsonLd?: (ctx: RouteContext) => Record<string, unknown> | null;
  render: (ctx: RouteContext) => string;
  mount?: (root: HTMLElement, ctx: RouteContext) => RouteUnmount | void;
  /**
   * For routes whose page the server sends (home, region hubs, glossary, the
   * price index, agent landings). On the FIRST resolve, when
   * `prerenderMarker` is found in `<main id="route-mount">`, the router keeps
   * that HTML instead of `mountEl.innerHTML = render(ctx)`, appends what
   * `hydrate(ctx)` returns (usually nothing) and calls `mount()` as usual.
   * Later SPA navigations to the route use `render()`.
   */
  hydrate?: (ctx: RouteContext) => string;
  /**
   * Data the page reads that loads after boot (the article catalogs). On an
   * in-app navigation the router waits for it, keeping the previous page on
   * screen, so the route never draws from a half-loaded catalog (2026-10-05:
   * a quick click to /ru/blog drew the English list). null = ready.
   */
  whenReady?: () => Promise<unknown> | null;
  /**
   * CSS selector for the server-rendered page. Router checks
   * `mount.querySelector(prerenderMarker)` on first resolve to decide
   * whether to use `hydrate()` or `render()`. Required if `hydrate` is set.
   */
  prerenderMarker?: string;
  /**
   * Force `<meta name="robots" content="noindex,follow">` regardless of
   * authRequired. Used as a stop-gap for content that's published but
   * not yet ready for search-engine indexing (e.g. country pages while
   * we rebuild templated bodies — 2026-05-04).
   *
   * Pass `true` to noindex unconditionally, or a function for per-slug
   * decisions (e.g. staged un-noindex where only top-N tourist countries
   * are indexable at the current stage). Returning `false` from the
   * function means "this route renders an indexable page".
   *
   * `follow` (vs `nofollow`) preserves internal link-equity flow so when
   * we unblock indexing later, the link graph is already warm.
   */
  noindex?: boolean | ((ctx: RouteContext) => boolean);
}

interface CompiledRoute extends RouteDef {
  pattern: RegExp;
  paramNames: string[];
}

function compile(path: string): { pattern: RegExp; paramNames: string[] } {
  const names: string[] = [];
  const re = path
    .replace(/\/$/, '')
    .replace(/:(\w+)/g, (_m, n: string) => {
      names.push(n);
      return '([^/]+)';
    });
  return { pattern: new RegExp(`^${re || '/'}$`), paramNames: names };
}

let routes: CompiledRoute[] = [];
let notFound: CompiledRoute | null = null;
let currentUnmount: RouteUnmount | null = null;
// Track first route resolution per page-load. Skip focus-on-mount the
// first time so the user's natural landing position isn't yanked.
// Set to false after the first resolve; subsequent SPA navigations
// move focus to the new <h1> (a11y best practice). 2026-05-09.
let isFirstResolve = true;
let mountEl: HTMLElement | null = null;

/**
 * Returns the canonical (locale-stripped) path for matching against the route
 * table. `/ru/prices` → `/prices`. Locale itself is read separately and
 * applied to the i18n bundle.
 */
function currentPath(): string {
  let p = window.location.pathname || '/';
  if (!p.startsWith('/')) p = '/' + p;
  p = p.replace(/\/+$/, '') || '/';
  return stripLocale(p);
}

/**
 * The browser-bar path including the locale prefix — used for canonical URL
 * and navigation state (we don't want to strip /ru when computing canonical).
 */
function rawPath(): string {
  let p = window.location.pathname || '/';
  if (!p.startsWith('/')) p = '/' + p;
  return p.replace(/\/+$/, '') || '/';
}

function match(path: string): { route: CompiledRoute; params: Record<string, string> } | null {
  for (const r of routes) {
    const m = r.pattern.exec(path);
    if (m) {
      const params: Record<string, string> = {};
      // m[i+1] is the captured group — TS sees `string | undefined` because
      // RegExp groups can be optional. Capture is mandatory in our patterns
      // (no `(...)?`), but coalesce defensively.
      r.paramNames.forEach((n, i) => (params[n] = decodeURIComponent(m[i + 1] ?? '')));
      return { route: r, params };
    }
  }
  return null;
}

/**
 * The social card for a canonical (locale-stripped) path, the same choice the
 * server makes in meta.ts cardImageFor(): blog posts and country pages have
 * their own card from scripts/build-og.mjs, every other page the default.
 * Until 2026-10-04 this named cards for /, /prices, /faq, /guides and /blog
 * that the server never used.
 */
function ogImageForPath(path: string): string {
  const m = path.replace(/^\//, '').match(/^(countries|blog)\/([a-z0-9-]+)$/);
  return m ? `/og/${m[1]}-${m[2]}.png` : '/og-default.png';
}

/**
 * True when the server already wrote this page's meta tags and the client has
 * not navigated yet.
 *
 * SSR emits localized, per-URL titles and descriptions and stamps
 * <html data-ssr-meta>. Routes that build their own title (country, blog post,
 * guides, about) must not overwrite that on first paint: the audit found them
 * reverting Russian titles to English, replacing the country keyword title with
 * "<h1> — Roamzy", and collapsing region hubs onto one string. After a
 * client-side navigation there is no SSR, so the route must set them normally.
 *
 * Kept here because `isFirstResolve` lives here and there must be exactly one
 * answer to "is this still the server-rendered paint".
 */
const initialPath = typeof location !== 'undefined' ? location.pathname : '';

/**
 * The server-rendered body already on the page, if it was rendered for the
 * current URL.
 *
 * SSR routes keep that body when the router re-resolves the same URL
 * (Auth.onChange settling, popstate). They used to test only for their marker
 * class, which a DIFFERENT page of the same kind also carries: /guides →
 * /guides/ios, one region → another, /ai-agents → /mcp-server all changed the
 * URL and left the previous page on screen (2026-09-30; the operator: "these
 * buttons lead nowhere"). The router stamps the path it rendered for.
 */
export function ssrBodyFor(marker: string): string | null {
  if (typeof document === 'undefined') return null;
  const mount = document.getElementById('route-mount');
  if (!mount || !mount.querySelector(marker)) return null;
  // The full browser path, locale prefix included: switching language keeps
  // the unprefixed path, and must not keep the old language's body.
  return mount.dataset.renderedPath === rawPath() ? mount.innerHTML : null;
}

/**
 * The browser path with its locale prefix, for routes that reload into a
 * server-rendered page. They used the router's unprefixed `ctx.path`, so an
 * in-app click from /ru to a region or an agent page loaded the English one.
 */
export function browserPath(): string {
  return rawPath();
}

export function ssrOwnsMeta(): boolean {
  if (typeof document === 'undefined') return false;
  // Tied to the URL, not to a render counter. The router resolves the initial
  // URL more than once (locale sync re-entry), and routes apply their titles
  // from queueMicrotask, so any "first pass" flag was already false by the time
  // it was consulted. While the user is still on the URL the server rendered,
  // the server's localized meta are the correct ones; the moment they navigate,
  // there is no SSR for the new URL and the route must set them itself.
  return location.pathname === initialPath
    && document.documentElement.hasAttribute('data-ssr-meta');
}

function setMeta(route: CompiledRoute, ctx: RouteContext): void {
  document.title = route.metaTitle;
  const desc = document.querySelector('meta[name="description"]');
  if (desc && route.metaDesc) desc.setAttribute('content', route.metaDesc);
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute('content', route.metaTitle);
  const ogDesc = document.querySelector('meta[property="og:description"]');
  if (ogDesc && route.metaDesc) ogDesc.setAttribute('content', route.metaDesc);
  const ogType = document.querySelector('meta[property="og:type"]');
  if (ogType) ogType.setAttribute('content', route.routeType === 'article' ? 'article' : 'website');
  // A post's article:published_time / modified_time come from the server for
  // the page it rendered; after a navigation they would name another page.
  document.querySelectorAll('meta[property^="article:"]').forEach((m) => m.remove());
  const canon = document.querySelector('link[rel="canonical"]');
  if (canon) {
    const localePath = withLocale(ctx.path === '/' ? '/' : ctx.path);
    canon.setAttribute('href', `https://roamzy.io${localePath === '/' ? '/' : localePath}`);
  }
  // Per-route social card, as the server chose it for the first paint.
  const ogImageUrl = `https://roamzy.io${ogImageForPath(ctx.path)}`;
  const ogImage = document.querySelector('meta[property="og:image"]');
  if (ogImage) ogImage.setAttribute('content', ogImageUrl);
  const twImage = document.querySelector('meta[name="twitter:image"]');
  if (twImage) twImage.setAttribute('content', ogImageUrl);
  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) {
    const localePath = withLocale(ctx.path === '/' ? '/' : ctx.path);
    ogUrl.setAttribute('content', `https://roamzy.io${localePath === '/' ? '/' : localePath}`);
  }
  // hreflang siblings — emit/replace per route change
  applyHreflangLinks(ctx.path === '/' ? '/' : ctx.path);
  const robots = document.getElementById('meta-robots');
  if (robots) {
    const noindexResolved = typeof route.noindex === 'function'
      ? route.noindex(ctx)
      : !!route.noindex;
    const robotsContent =
      route.authRequired ? 'noindex,nofollow'
      : noindexResolved ? 'noindex,follow'
      : 'index,follow';
    robots.setAttribute('content', robotsContent);
  }
  // JSON-LD is now SSR'd per-route by backend/util/json-ld.ts (single
  // source of truth since 2026-05-07). Client-side overwriting was racing
  // with the server payload and had its own '001'-style bugs that didn't
  // get caught because they were JS-only. We leave whatever the server
  // injected on the initial page load. SPA route changes don't update
  // <script id="ld-json"> — Googlebot and AI crawlers always do fresh
  // fetches per URL anyway, so the in-browser JSON-LD doesn't influence
  // indexing for client-side navigations.
  // fix-5: per-route locale meta
  const ogLocale = document.getElementById('meta-og-locale');
  if (ogLocale) ogLocale.setAttribute('content', OG_LOCALE[getLocale()] ?? OG_LOCALE.en);
  const ogImageAlt = document.querySelector('meta[property="og:image:alt"]');
  if (ogImageAlt) ogImageAlt.setAttribute('content', route.metaTitle);
  const twTitle = document.getElementById('meta-twitter-title');
  if (twTitle) twTitle.setAttribute('content', route.metaTitle);
  const twDesc = document.getElementById('meta-twitter-desc');
  if (twDesc && route.metaDesc) twDesc.setAttribute('content', route.metaDesc);
  // <html lang> mirrors active locale so screen readers / Google match correctly
  document.documentElement.setAttribute('lang', getLocale());
}

// FIX a11y: aria-current page
// Mirror the active class with `aria-current="page"` so screen readers and
// assistive tech can identify the current location. Done here (router) rather
// than in main.ts because this is the single place we know the active nav key
// for the current route.
function setActiveNav(navKey: string | undefined): void {
  document.querySelectorAll('[data-nav]').forEach((el) => {
    el.classList.remove('nav__link--active');
    el.removeAttribute('aria-current');
  });
  if (navKey) {
    const el = document.querySelector(`[data-nav="${navKey}"]`);
    if (el) {
      el.classList.add('nav__link--active');
      el.setAttribute('aria-current', 'page');
    }
  }
}

// Track the last resolved path so we can tell a real route change apart
// from a no-op resolve (e.g. Auth.onChange firing during a top-up submit).
// Audit +23.1: blanket closeAll on every resolve() killed the topup modal
// mid-submit because `Auth.updateUserLocal({ email })` → emit() →
// Router.onChange(resolve) → closeAll. The modal closed before
// `window.location.href = invoiceUrl` could fire — operator caught
// «перестала брониться и отображаться eSIM» 2026-05-10 ночь.
let lastResolvedPath: string | null = null;

/**
 * Telegram Mini App BackButton sync.
 *
 * One BackButton frame per non-root route. Pushed on first non-root
 * resolve, popped when we land back on '/'. Inside a modal the modal
 * pushes its own frame ON TOP of this one (via ModalManager.open) — so
 * BackButton inside a modal closes the modal first, then the route's
 * frame becomes top and BackButton goes to history.back.
 *
 * No-op outside Telegram (the tgBackButton.push wrapper short-circuits).
 */
let routeBackUnmount: (() => void) | null = null;
// The screen the Mini App opened on has nothing behind it: history.back()
// there leads nowhere. BackButton used to show on every non-'/' route, so a
// Mini App launched straight into the dashboard offered "Back" on its first
// screen (2026-09-29). It stays hidden on the entry screen until the person
// has moved; '/dashboard' only redirects, so it never counts as the entry.
let tgEntryPath: string | null = null;
let tgLeftEntry = false;
function syncTgBackButton(canonicalPath: string): void {
  if (canonicalPath === '/dashboard') return;
  if (tgEntryPath === null) tgEntryPath = canonicalPath;
  else if (canonicalPath !== tgEntryPath) tgLeftEntry = true;
  const onRoot = canonicalPath === '/' || (!tgLeftEntry && canonicalPath === tgEntryPath);
  if (onRoot) {
    if (routeBackUnmount) {
      routeBackUnmount();
      routeBackUnmount = null;
    }
    return;
  }
  if (!routeBackUnmount) {
    routeBackUnmount = tgBackButton.push(() => {
      tgHaptic.tapLight();
      window.history.back();
    });
  }
}

function resolve(): void {
  if (!mountEl) return;
  const newPath = rawPath();
  const pathChanged = newPath !== lastResolvedPath;
  // Close modals ONLY on a real path change. Modals are page-scoped UI; a
  // leftover modal on top of a freshly-routed page reads as a bug (caught
  // 2026-05-10 +23: QR modal stayed open after clicking «Full setup
  // guides»). But a same-path resolve (e.g. fired by Auth.onChange after
  // updateUserLocal during a topup submit) MUST NOT touch open modals —
  // the user is still interacting with them. The auth-redirect path below
  // dispatches `roamzy:open-login` after this hook, so a fresh auth modal
  // opens on top of a clean page when an actual nav happens — no stacking.
  if (pathChanged) ModalManager.closeAll();
  lastResolvedPath = newPath;
  // Sync i18n locale to URL prefix on every navigation (forward/back/click).
  const urlLocale = localeFromPath(rawPath());
  if (urlLocale !== getLocale()) setLocale(urlLocale);
  // Capture utm_* query params + send anonymous /api/utm beacon. First-touch
  // attribution: stored in localStorage, picked up by auth POSTs at signup.
  // Idempotent — safe on every navigation.
  captureUtm();

  const path = currentPath();
  const m = match(path) ?? (notFound ? { route: notFound, params: {} } : null);
  if (!m) return;

  const { route, params } = m;
  const ctx: RouteContext = { path, params };

  if (route.authRequired && !Auth.isAuthed()) {
    // Preserve locale on auth-redirect: pass the LOCALE-PREFIXED path so when
    // the modal does pushState(redirectTo) the user lands back in their language.
    document.dispatchEvent(new CustomEvent('roamzy:open-login', { detail: { redirectTo: rawPath() } }));
    return;
  }

  // A server page kept on first paint needs nothing; anything drawn here does.
  const keepsServerPage = isFirstResolve && !!route.hydrate && !!route.prerenderMarker
    && !!mountEl.querySelector(route.prerenderMarker);
  const pending = keepsServerPage ? null : route.whenReady?.() ?? null;
  if (pending) {
    void pending.then(() => { if (rawPath() === newPath) resolve(); });
    return;
  }

  if (currentUnmount) {
    try {
      currentUnmount();
    } catch {
      /* ignore */
    }
    currentUnmount = null;
  }

  // First resolve of a page the server sent: keep its HTML (see `hydrate`).
  const served = isFirstResolve && route.hydrate && route.prerenderMarker
    ? mountEl.querySelector(route.prerenderMarker)
    : null;
  if (served && route.hydrate) {
    mountEl.insertAdjacentHTML('beforeend', route.hydrate(ctx));
  } else {
    mountEl.innerHTML = route.render(ctx);
  }
  mountEl.dataset.renderedPath = newPath;
  // Skip the meta pass on the very first paint when the server already wrote
  // them (it stamps <html data-ssr-meta>). Those tags are localized and
  // SEO-tuned per URL; setMeta() rebuilds them from the static route table,
  // which is English-only and generic — it used to revert localized titles to
  // English, collapse the nine region hubs onto one title, drop country
  // keyword h1s and replace blog SEO titles with "<h1> · Roamzy Journal".
  // Client-side navigations still need it: there is no SSR on those.
  // The server-rendered meta stay authoritative until the user actually
  // navigates. Routes apply their own titles inside queueMicrotask, i.e. after
  // this function returns, so releasing the marker at the end of the first
  // resolve would hand them a stale answer and they would overwrite the
  // localized SSR title anyway (exactly what the audit caught).
  if (!ssrOwnsMeta()) setMeta(route, ctx);
  setActiveNav(route.navKey);
  // A11y: move focus to the new content's heading after SPA navigation.
  // Without this, the link the user clicked is gone but focus lands
  // on <body>, leaving screen readers silent. Skipped on the initial
  // page load (`isFirstResolve`) — there the user just landed and
  // their natural focus position is fine. Operator-driven a11y pass
  // 2026-05-09. Uses tabindex=-1 on <main id="route-mount"> so the
  // element can take programmatic focus without staying tabbable.
  if (!isFirstResolve) {
    const heading = mountEl.querySelector<HTMLElement>('h1');
    const target = heading ?? mountEl;
    // Make heading temporarily focusable, then restore. This keeps Tab
    // order natural for keyboard users while still moving focus there
    // for screen readers on the navigation.
    const hadTabindex = target.hasAttribute('tabindex');
    if (!hadTabindex) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    if (!hadTabindex) {
      // Remove on next blur so we don't leave stray tabindex in DOM.
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    }
  }
  isFirstResolve = false;

  // Apply DB-backed SEO overrides + global settings (noindex, default
  // OG image, JSON-LD) on every route — runs after the static setMeta()
  // so admin's per-page overrides win. Cached 60s. Variants B+C 2026-05-06.
  void import('./seo-overrides').then(({ applySeoOverrides }) => {
    void applySeoOverrides(currentPath());
  });

  const close = document.getElementById('mobile-menu');
  if (close) close.classList.remove('is-open', 'open');

  if (route.mount) {
    const unmount = route.mount(mountEl, ctx);
    if (typeof unmount === 'function') currentUnmount = unmount;
  }

  // Telegram Mini App: show/hide BackButton based on whether we're on
  // the locale-stripped root path. Called every resolve so SPA navigation
  // (forward and back) keeps the button state correct. No-op on web.
  syncTgBackButton(path);

  window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
}

/**
 * A link to a part of this page (a post's "Contents") is the browser's to
 * follow: routing it would redraw the page and scroll back to the top.
 */
function isSamePageAnchor(url: URL): boolean {
  return !!url.hash && url.pathname === window.location.pathname && url.search === window.location.search;
}

function isInternalLink(a: HTMLAnchorElement): boolean {
  if (!a.href) return false;
  if (a.target && a.target !== '_self') return false;
  if (a.hasAttribute('download')) return false;
  if (a.getAttribute('rel') === 'external') return false;
  const url = new URL(a.href, window.location.href);
  if (url.origin !== window.location.origin) return false;
  if (isSamePageAnchor(url)) return false;
  if (url.pathname.startsWith('/api/')) return false;
  // Static .html files (e.g. /launch.html, /launch-en.html) are NOT SPA
  // routes — all real SPA paths are extensionless (`/prices`, `/blog/x`).
  // Treat any `.html` URL as a full-navigation target so the browser fetches
  // the static file directly. Without this the SPA router intercepts the
  // click, tries to match `/launch.html` against compiled routes, fails,
  // and renders the 404 component while the actual file sits one HTTP GET
  // away. Operator caught this 2026-05-13 right after v333 SEO pass dropped
  // `rel="external"` from the footer launch link.
  if (url.pathname.endsWith('.html')) return false;
  return true;
}

function onClick(e: MouseEvent): void {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = (e.target as Element | null)?.closest('a');
  if (!a || !(a instanceof HTMLAnchorElement)) return;
  if (!isInternalLink(a)) return;
  const url = new URL(a.href, window.location.href);
  e.preventDefault();
  // Re-localize the destination so canonical anchors (`/countries/x`) keep the
  // user in their current language. `withLocale` is idempotent — already-prefixed
  // links stay (after one round of strip+prepend with current locale).
  // Note: cross-locale jumps go through the language picker, never through anchors.
  const localizedPath = withLocale(url.pathname);
  const target = localizedPath + url.search + url.hash;
  if (target !== window.location.pathname + window.location.search + window.location.hash) {
    window.history.pushState({}, '', target);
  }
  resolve();
}

export const Router = {
  init(defs: RouteDef[]): void {
    mountEl = document.getElementById('route-mount');
    if (!mountEl) throw new Error('Router: #route-mount not found');
    routes = defs
      .filter((d) => d.path !== '*')
      .map((d) => ({ ...d, ...compile(d.path) }));
    const nf = defs.find((d) => d.path === '*');
    notFound = nf ? { ...nf, ...compile('/__notfound__') } : null;

    window.addEventListener('popstate', () => resolve());
    document.addEventListener('click', onClick);
    Auth.onChange(() => resolve());

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => resolve());
    } else {
      resolve();
    }
  },

  navigate(path: string): void {
    // path may be canonical (`/prices`) or already-locale-prefixed (`/ru/prices`).
    // Always normalize to current locale before pushing.
    const target = withLocale(path);
    if (target === window.location.pathname) return;
    window.history.pushState({}, '', target);
    resolve();
  },

  resolve,
};
